import React from 'react';
import { Badge, Button, Dropdown, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SearchableSelect from '../common/SearchableSelect';
import { formatCurrency, formatUtcDate } from '../../utils/formatters';

const TransactionRow = ({ 
  tx, 
  accounts, 
  payees, 
  classes, 
  isEditor, 
  onUpdate, 
  onDelete, 
  onSelect, 
  isSelected, 
  plTypes, 
  bsTypes, 
  onEditJournal,
  onCreateRule,
  onAddPayee,
  onAddClass,
  onAddCategory,
  columnConfig = { showPayee: true, showClass: true, showNote: true },
  selectedAccountId,
  isUpdating = false
}) => {
  if (!tx) return null;

  const isTransferInit = tx.toAccount && bsTypes.includes(tx.toAccount.type);

  // The amount is already signed correctly by the backend API relative to the selected account register
  const displayAmount = tx.amount;

  return (
    <React.Fragment>
      <tr>
        <td className="align-middle pr-0" style={{ width: '30px' }}>
          <input type="checkbox" checked={isSelected} onChange={() => onSelect(tx._id)} />
        </td>
        <td className="small align-middle">
          {tx.type === 'Journal Entry' || !isEditor ? (
            formatUtcDate(tx.date)
          ) : (
            <input 
              type="date"
              value={tx.date ? tx.date.slice(0, 10) : ''}
              onChange={(e) => onUpdate(tx._id, { date: e.target.value }, accounts)}
              className="form-control form-control-sm border-0 bg-transparent p-0"
              style={{ fontSize: '0.75rem', cursor: 'pointer', width: '115px' }}
            />
          )}
        </td>
        <td className="align-middle" style={{ maxWidth: '300px', overflow: 'hidden' }}>
          <div className="small text-dark d-flex align-items-center" style={{ gap: '6px' }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`tooltip-${tx._id}`}>{tx.description}</Tooltip>}
            >
              <span className="font-weight-medium text-truncate mr-1" style={{ flex: '1', minWidth: '0', cursor: 'help' }}>
                {tx.description}
              </span>
            </OverlayTrigger>
            {tx.type === 'Journal Entry' && (
              <Badge 
                variant="secondary" 
                style={{ fontSize: '0.6rem', cursor: 'pointer', padding: '2px 4px' }}
                onClick={() => onEditJournal(tx.journalEntryId)}
                title="Click to view/edit journal entry"
              >
                JE
              </Badge>
            )}
            {isEditor && tx.type !== 'Journal Entry' && (
              <select
                value={tx.clearedStatus || 'Uncleared'}
                onChange={(e) => onUpdate(tx._id, { clearedStatus: e.target.value }, accounts)}
                className="border-0 bg-transparent text-muted font-weight-bold ml-1 p-0"
                style={{ 
                  fontSize: '0.65rem', 
                  cursor: 'pointer',
                  outline: 'none',
                  borderBottom: '1px dotted #cbd5e1'
                }}
              >
                <option value="Uncleared">Uncleared</option>
                <option value="Cleared">Cleared</option>
                <option value="Reconciled">Reconciled</option>
              </select>
            )}
          </div>
        </td>
        <td className={`text-right align-middle small ${(displayAmount || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
          {formatCurrency(displayAmount || 0)}
        </td>
        {(columnConfig.showPayee || columnConfig.showClass) && (
          <td className="align-middle" style={{ minWidth: '150px' }}>
            {!isEditor || tx.type === 'Journal Entry' ? (
              <>
                {columnConfig.showPayee && (
                  <div className="mb-1 small text-dark">
                    {tx.payeeId ? tx.payeeId.name : 'No Payee'}
                  </div>
                )}
                {columnConfig.showClass && (
                  <div className="small text-muted">
                    Class: {tx.classId ? tx.classId.name : '-'}
                  </div>
                )}
              </>
            ) : (
              <>
                {columnConfig.showPayee && (
                  <div className="mb-1">
                    <SearchableSelect
                      options={[
                        { value: '', label: '-- Payee --' },
                        ...(payees ? payees.filter(p => p.isActive).map(p => ({ value: p._id, label: p.name })) : [])
                      ]}
                      value={tx.payeeId ? (typeof tx.payeeId === 'string' ? tx.payeeId : tx.payeeId._id) : ''}
                      onChange={(e) => onUpdate(tx._id, { payeeId: e.target.value || null }, accounts)}
                      placeholder="-- Payee --"
                      onAddCustom={async (name) => {
                        const newPayee = await onAddPayee(name);
                        if (newPayee) {
                          onUpdate(tx._id, { payeeId: newPayee._id }, accounts);
                        }
                      }}
                    />
                  </div>
                )}
                {columnConfig.showClass && (
                  <div>
                    <SearchableSelect
                      options={[
                        { value: '', label: '-- Class --' },
                        ...(classes ? classes.filter(c => c.isActive).map(c => ({ value: c._id, label: c.name })) : [])
                      ]}
                      value={tx.classId ? (typeof tx.classId === 'string' ? tx.classId : tx.classId._id) : ''}
                      onChange={(e) => onUpdate(tx._id, { classId: e.target.value || null }, accounts)}
                      placeholder="-- Class --"
                      onAddCustom={async (name) => {
                        const newClass = await onAddClass(name);
                        if (newClass) {
                          onUpdate(tx._id, { classId: newClass._id }, accounts);
                        }
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </td>
        )}
        <td className="align-middle" style={{ minWidth: '150px' }}>
          {(() => {
            const activeAccount = accounts.find(a => a._id === selectedAccountId);
            const isActiveCreditCard = activeAccount && activeAccount.type === 'Credit Card';
            const isDeposit = tx.amount >= 0;
            const isCategoryInFromAccount = isActiveCreditCard ? !isDeposit : isDeposit;
            
            const categoryAccount = isCategoryInFromAccount ? tx.fromAccount : tx.toAccount;
            const categoryLabel = categoryAccount ? categoryAccount.name : 'Uncategorized';
            const currentCategoryAccountId = categoryAccount ? (categoryAccount._id || categoryAccount) : '';

            // Determine which account is the bank account to exclude it from the Transfer options dropdown list
            const bankAccountRefId = isCategoryInFromAccount 
              ? (tx.toAccount?._id || tx.toAccount) 
              : (tx.fromAccount?._id || tx.fromAccount);

            if (!isEditor || tx.type === 'Journal Entry') {
              return (
                <Badge variant={isTransferInit ? "info" : "success"}>
                  {categoryLabel}
                </Badge>
              );
            }

            return (
              <SearchableSelect
                options={[
                  { value: '', label: '-- Uncategorized --' },
                  ...accounts.filter(a => plTypes.includes(a.type)).map(acc => ({ value: acc._id, label: acc.name, sublabel: `Category (${acc.type})` })),
                  ...accounts.filter(a => bsTypes.includes(a.type) && a._id !== bankAccountRefId).map(acc => ({ value: acc._id, label: acc.name, sublabel: `Transfer (${acc.type})` }))
                ]}
                value={currentCategoryAccountId}
                onChange={(e) => onUpdate(tx._id, { toAccountId: e.target.value || null }, accounts)}
                placeholder="-- Category --"
                onAddCustom={async (name) => {
                  const defaultType = isDeposit ? 'Income' : 'Expense';
                  const newCategory = await onAddCategory(name, defaultType);
                  if (newCategory) {
                    onUpdate(tx._id, { toAccountId: newCategory._id, category: newCategory.name }, accounts);
                  }
                }}
              />
            );
          })()}
        </td>
        {columnConfig.showNote && (
          <td className="align-middle text-wrap" style={{ maxWidth: '200px' }}>
            {!isEditor ? (
              <span className="small text-muted">{tx.note || 'No note'}</span>
            ) : (
              <input 
                type="text"
                defaultValue={tx.note || ''}
                onBlur={(e) => {
                  if (e.target.value !== (tx.note || '')) {
                    onUpdate(tx._id, { note: e.target.value }, accounts);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                placeholder="Click to add note..."
                className="form-control form-control-sm border-0 bg-transparent p-0 small text-muted text-truncate w-100"
                style={{ fontSize: '0.75rem', outline: 'none', boxShadow: 'none' }}
              />
            )}
          </td>
        )}
        <td className="align-middle text-center">
          {isEditor && (
            <Dropdown as={ButtonGroup} size="sm">
              {tx.excluded ? (
                <Button 
                  variant="outline-info" 
                  className="py-0 px-2 font-weight-bold d-inline-flex align-items-center" 
                  style={{ fontSize: '0.65rem' }}
                  onClick={() => onUpdate(tx._id, { excluded: false }, accounts)}
                  title="Restore transaction to Pending status"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1"></i> Restoring...
                    </>
                  ) : (
                    'Restore'
                  )}
                </Button>
              ) : tx.posting === false ? (
                <Button 
                  variant="success" 
                  className="py-0 px-2 font-weight-bold d-inline-flex align-items-center" 
                  style={{ fontSize: '0.65rem' }}
                  onClick={() => onUpdate(tx._id, { posting: true }, accounts)}
                  title="Post transaction to impact report"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1"></i> Posting...
                    </>
                  ) : (
                    'Post'
                  )}
                </Button>
              ) : (
                <Button 
                  variant="outline-danger" 
                  className="py-0 px-2 font-weight-bold d-inline-flex align-items-center" 
                  style={{ fontSize: '0.65rem' }}
                  onClick={() => onDelete(tx)}
                  title={
                    (tx.type === 'Imported' || tx.plaidTransactionId || tx.documentId)
                      ? "Unpost / Move to Pending"
                      : "Delete Transaction"
                  }
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1"></i> Saving...
                    </>
                  ) : (
                    'Delete'
                  )}
                </Button>
              )}

              <Dropdown.Toggle 
                split 
                variant={tx.excluded ? "outline-info" : (tx.posting === false ? "success" : "outline-danger")} 
                id={`dropdown-split-basic-${tx._id}`}
                className="py-0 px-1"
                style={{ fontSize: '0.65rem' }}
                disabled={isUpdating}
              />

              <Dropdown.Menu 
                align="right" 
                style={{ fontSize: '0.75rem', minWidth: '100px' }}
                popperConfig={{ strategy: 'fixed' }}
              >
                {tx.posting === false && !tx.excluded && (
                  <Dropdown.Item 
                    className="text-danger" 
                    onClick={() => onDelete(tx)}
                  >
                    Exclude
                  </Dropdown.Item>
                )}
                
                {tx.excluded && (tx.type !== 'Imported' && !tx.plaidTransactionId && !tx.documentId) && (
                  <Dropdown.Item 
                    className="text-danger" 
                    onClick={() => onDelete(tx)}
                  >
                    Delete Permanently
                  </Dropdown.Item>
                )}

                {tx.posting === true && !tx.excluded && (
                  <Dropdown.Item 
                    className="text-danger" 
                    onClick={() => onDelete(tx)}
                  >
                    Unpost
                  </Dropdown.Item>
                )}

                {tx.type !== 'Journal Entry' && (
                  <Dropdown.Item onClick={() => onCreateRule(tx)}>
                    Create Rule
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </td>
      </tr>
    </React.Fragment>
  );
};

export default TransactionRow;
